import { createSlice } from "@reduxjs/toolkit";
import FindOrCreateShopkeeperService from "../services/findOrCreateShopkeeper";
import {
  addSaga,
  createAsyncSaga,
  createSagaReducer,
  persist,
  sagaSlice,
} from "./sagaStore";

const initialState: any = {
  // Step 0: Shopkeeper identification
  shopkeeper: {
    loading: false,
    error: null,
    data: {},
  },
  // Current step in the wizard (1-indexed: 1 = Basic Info)
  currentStep: 1,
  // Form data collected across all steps
  formData: {
    // Step 0 gate result
    shopkeeperEmail: "",
    shopkeeperFirebaseUid: "",
    isNewUser: false,
    // Step 1: Basic Shop Info
    shopImage: "",
    shopImages: [] as string[],
    shopName: "",
    shopSlug: "",
    shopSlugAvailable: null as boolean | null,
    shopBillingSoftware: "",
    shopDescription: "",
    // Step 2: Category
    shopCategories: [] as string[],
    // Step 3: Address
    addressId: null as number | null,
    street: "",
    locality: "",
    landmark: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    lat: null as number | null,
    lng: null as number | null,
    // Step 4: WhatsApp
    whatsappCountryCode: "+91",
    whatsappNumber: "",
    // Step 5: Store Timings
    openDays: {
      Sunday: true,
      Monday: true,
      Tuesday: true,
      Wednesday: true,
      Thursday: true,
      Friday: true,
      Saturday: true,
    },
    openingTime: "0730",
    closingTime: "2200",
    // Step 6: Order & Delivery Details
    isReturnable: false,
    isCancellable: false,
    minOrderValue: 100,
    deliveryRadius: 5,
    orderTat: "P1D",
    timeToShip: "PT12H",
    isConvenienceFeeBornBySeller: false,
    minOrderForFreeDelivery: 999,
    isFixedDeliveryFee: true,
    deliveryFee: 50,
    // Step 7: Shop Configuration
    // Holds the AntD Form values shape from ShopConfigStep (mirrors the
    // panels used by ShopConfigSection). Populated on step entry with panel
    // defaults; transformed via `transformShopConfigFormToPayload` inside
    // `buildShopPayload` when the shop is finally created.
    shopConfig: {} as Record<string, unknown>,
  },
  // Track validity of each step
  stepValidity: {
    step0: false, // Shopkeeper identified
    step1: false, // Basic Info
    step2: false, // Address
    step3: false, // WhatsApp
    step4: false, // Timings
    step5: false, // Order & Delivery
    step6: false, // Shop Configuration
  },
};

const { name, initialState: persistedState } = persist(
  "shopOnboarding",
  initialState
);

const Slice: sagaSlice = createSlice({
  name,
  initialState: persistedState,
  reducers: {
    ...createSagaReducer("findOrCreateShopkeeper", "shopkeeper", {
      success: (state: any, action: any) => {
        const result = action.payload?.result?.data;
        if (result) {
          state.formData.shopkeeperEmail = result.email;
          state.formData.shopkeeperFirebaseUid = result.firebaseUid;
          state.formData.isNewUser = result.isNewUser;
          state.stepValidity.step0 = true;
        }
      },
    }),
    // Set current step
    setCurrentStep: (state: any, action: any) => {
      state.currentStep = action.payload;
    },
    // Update form data for a specific step
    updateFormData: (state: any, action: any) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    // Update step validity
    updateStepValidity: (state: any, action: any) => {
      const { step, isValid } = action.payload;
      state.stepValidity[step] = isValid;
    },
    // Reset entire onboarding state (after successful creation or "Create Another")
    resetOnboarding: () => {
      // Clear persisted state
      localStorage.removeItem("persist:shopOnboarding");
      return initialState;
    },
  },
});

const findOrCreateShopkeeperSaga = createAsyncSaga(
  "findOrCreateShopkeeper",
  Slice,
  FindOrCreateShopkeeperService.findOrCreate,
  ["email"]
);

addSaga(Slice.actions.findOrCreateShopkeeper.type, findOrCreateShopkeeperSaga, 0);

export const {
  findOrCreateShopkeeper,
  setCurrentStep,
  updateFormData,
  updateStepValidity,
  resetOnboarding,
} = Slice.actions;

export default Slice.reducer;
